import React, { useEffect, useMemo, useRef, useState } from "react";
import { BarChart3, LineChart, PieChart } from "lucide-react";
import { ODOO } from "./crmV2Helpers";

// Palette suffisamment longue pour que les différentes paires étape / commercial
// restent identifiables sans réutiliser prématurément une même couleur.
const COLORS = [
  "#4a86e8", "#f0ad4e", "#5cb85c", "#d9534f", "#9b59b6",
  "#17a2b8", "#e67e22", "#34495e", "#e76f9a", "#9c89b8",
  "#a8c5e6", "#76c893", "#ff9f80", "#bc8f8f", "#7f8c8d",
  "#277da1", "#90be6d", "#f94144", "#577590", "#c77dff",
];

const daysBetween = (from, to) => {
  if (!from || !to) return null;
  const start = new Date(from);
  const end = new Date(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.max(0, (end.getTime() - start.getTime()) / 86400000);
};

const measureValue = (lead, measure) => {
  const expectedRevenue = Number(lead.expected_revenue) || 0;
  const probability = Number(lead.probability) || 0;

  switch (measure) {
    case "expected_revenue": return expectedRevenue;
    case "probability": return probability;
    case "prorated_revenue": return expectedRevenue * probability / 100;
    case "days_to_assign":
      return daysBetween(lead.create_date || lead.created_at, lead.date_assign || lead.assign_date || lead.date_open);
    case "days_to_close":
      return daysBetween(lead.date_open || lead.create_date || lead.created_at, lead.date_closed);
    case "count":
    default: return 1;
  }
};

const AVERAGE_MEASURES = new Set(["probability", "days_to_assign", "days_to_close"]);

const useContainerSize = (ref) => {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        setSize({ w: Math.round(width), h: Math.round(height) });
      }
    });
    obs.observe(el);
    // initial
    const r = el.getBoundingClientRect();
    if (r.width) setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    return () => obs.disconnect();
  }, [ref]);
  return size;
};

const CrmV2GraphView = ({ leads, stages, userNameOf, loading }) => {
  const [measure, setMeasure] = useState("count");
  const [chartType, setChartType] = useState("bar");
  const [sortDesc, setSortDesc] = useState(true);
  const [measuresOpen, setMeasuresOpen] = useState(false);
  const chartAreaRef = useRef(null);
  const size = useContainerSize(chartAreaRef);

  const stageOrder = useMemo(
    () => stages.filter((s) => s.is_active !== false).sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)),
    [stages]
  );

  const { owners, stageSeries, pieSeries, maxY } = useMemo(() => {
    const byStageOwner = new Map(); // stageName -> Map(owner -> val)

    for (const l of leads) {
      const st = stages.find((s) => String(s.id) === String(l.stage_id));
      const stageName = st?.name || "No stage";
      const owner = userNameOf?.(l.user_id) || l.user_name || "Unassigned";
      let m = byStageOwner.get(stageName);
      if (!m) { m = new Map(); byStageOwner.set(stageName, m); }
      const cur = m.get(owner) || { sum: 0, values: 0 };
      const value = measureValue(l, measure);
      if (value !== null) {
        cur.sum += value;
        cur.values += 1;
      }
      m.set(owner, cur);
    }

    let labels = stageOrder.map((s) => s.name).filter((n) => byStageOwner.has(n));
    for (const n of byStageOwner.keys()) {
      if (!labels.includes(n)) labels.push(n);
    }

    const owners = [...new Set(
      labels.flatMap((stageName) => [...(byStageOwner.get(stageName) || new Map()).keys()])
    )];

    // Barres et courbes : une abscisse par étape, une série par commercial.
    const stageSeries = labels.map((stageName) => {
      const valuesByOwner = byStageOwner.get(stageName) || new Map();
      const parts = owners.map((owner) => {
        const values = valuesByOwner.get(owner) || { sum: 0, values: 0 };
        return AVERAGE_MEASURES.has(measure) && values.values > 0
          ? values.sum / values.values
          : values.sum;
      });
      return { label: stageName, parts, total: parts.reduce((sum, value) => sum + value, 0) };
    });

    // Camembert : une tranche pour chaque couple étape / commercial.
    const pieSeries = labels.flatMap((stageName) => {
      const valuesByOwner = byStageOwner.get(stageName) || new Map();
      return [...valuesByOwner.entries()].map(([owner, values]) => {
        const total = AVERAGE_MEASURES.has(measure) && values.values > 0
          ? values.sum / values.values
          : values.sum;
        return {
          label: `${stageName} / ${owner}`,
          total,
        };
      });
    });

    if (sortDesc) pieSeries.sort((a, b) => b.total - a.total);
    else pieSeries.sort((a, b) => a.total - b.total);
    const maxY = Math.max(1, ...stageSeries.map((s) => s.total));
    return { owners, stageSeries, pieSeries, maxY };
  }, [leads, stages, stageOrder, userNameOf, measure, sortDesc]);

  const series = chartType === "pie" ? pieSeries : stageSeries;
  const legendItems = chartType === "pie"
    ? pieSeries.map((item, index) => ({ label: item.label, color: COLORS[index % COLORS.length] }))
    : owners.map((owner, index) => ({ label: owner, color: COLORS[index % COLORS.length] }));

  // dimensions — responsive to container
  const pad = { top: 24, right: 24, bottom: 80, left: 56 };
  // container size, fallback to viewport-like values before observer fires
  const areaW = size.w || 800;
  const areaH = size.h || 420;

  // bar: besoin d'une largeur mini pour rester lisible, mais on étire pour remplir
  const minBarInnerW = Math.max(480, series.length * 72);
  const minSvgW = minBarInnerW + pad.left + pad.right;
  const svgW = Math.max(minSvgW, areaW);
  const svgH = Math.max(320, areaH);
  const innerW = svgW - pad.left - pad.right;
  const innerH = svgH - pad.top - pad.bottom;
  const barGap = 16;
  const barW = Math.max(28, (innerW - barGap * (series.length + 1)) / Math.max(1, series.length));

  const yTicks = 6;
  const tickVals = Array.from({ length: yTicks }, (_, i) => Math.round((maxY * 1.1 * i) / (yTicks - 1)));

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        background: "#fff",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* toolbar */}
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 16px",
          borderBottom: `1px solid ${ODOO.border}`,
          flexWrap: "wrap",
          background: "#fff",
        }}
      >
        <div style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setMeasuresOpen((o) => !o)}
            style={{
              background: ODOO.primary,
              color: "#fff",
              border: "none",
              borderRadius: 3,
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.04em",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            MEASURES ▾
          </button>
          {measuresOpen && (
            <>
              <div onClick={() => setMeasuresOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  zIndex: 1000,
                  background: "#fff",
                  border: `1px solid ${ODOO.border}`,
                  borderRadius: 3,
                  boxShadow: "0 4px 12px rgba(0,0,0,.12)",
                  marginTop: 2,
                  minWidth: 180,
                  overflow: "hidden",
                }}
              >
                {[
                  { id: "days_to_assign", label: "Days to Assign" },
                  { id: "days_to_close", label: "Days to Close" },
                  { id: "expected_revenue", label: "Expected Revenue" },
                  { id: "probability", label: "Probability" },
                  { id: "prorated_revenue", label: "Prorated Revenue" },
                  { id: "count", label: "Count", divider: true },
                ].map((m) => (
                  <React.Fragment key={m.id}>
                    {m.divider && <div style={{ borderTop: `1px solid ${ODOO.border}`, margin: "4px 0" }} />}
                    <button
                      type="button"
                      onClick={() => { setMeasure(m.id); setMeasuresOpen(false); }}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: measure === m.id ? ODOO.primarySoft : "transparent",
                        padding: "8px 12px",
                        fontSize: 13,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        color: measure === m.id ? ODOO.primary : ODOO.text,
                        fontWeight: measure === m.id ? 600 : 400,
                      }}
                    >
                      {measure === m.id ? "✓ " : "   "}{m.label}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
        </div>

        {[
          { id: "bar", icon: BarChart3 },
          { id: "line", icon: LineChart },
          { id: "pie", icon: PieChart },
        ].map(({ id, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setChartType(id)}
            style={{
              width: 32,
              height: 32,
              border: `1px solid ${chartType === id ? ODOO.primary : ODOO.border}`,
              background: chartType === id ? ODOO.primarySoft : "#fff",
              borderRadius: 3,
              cursor: "pointer",
              color: chartType === id ? ODOO.primary : ODOO.muted,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={15} />
          </button>
        ))}

        <button
          type="button"
          onClick={() => setSortDesc((s) => !s)}
          title="Sort"
          style={{
            border: `1px solid ${ODOO.border}`,
            background: "#fff",
            borderRadius: 3,
            padding: "6px 10px",
            fontSize: 12,
            cursor: "pointer",
            fontFamily: "inherit",
            color: ODOO.muted,
          }}
        >
          {sortDesc ? "↓ Desc" : "↑ Asc"}
        </button>

        {loading && <span style={{ fontSize: 12, color: ODOO.muted }}>Loading…</span>}
      </div>

      {/* La légende s'adapte au type de graphique affiché. */}
      <div style={{ flexShrink: 0, display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center", padding: "10px 16px 4px", background: "#fff" }}>
        {legendItems.map((item) => (
          <span key={item.label} title={item.label} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: ODOO.text }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color }} />
            {item.label}
          </span>
        ))}
      </div>

      {/* Chart area — remplit intégralement le reste */}
      <div
        ref={chartAreaRef}
        style={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: chartType === "bar" || chartType === "line" ? "auto" : "hidden",
          background: "#fff",
          position: "relative",
        }}
      >
        {series.length === 0 ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: ODOO.muted, fontSize: 13, padding: 40 }}>
            No data to display
          </div>
        ) : chartType === "pie" ? (
          <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", overflow: "auto" }}>
            <PieResponsive series={series} areaW={areaW} areaH={areaH} />
          </div>
        ) : chartType === "line" ? (
          <LineResponsive series={series} owners={owners} maxY={maxY} svgW={svgW} svgH={svgH} pad={pad} />
        ) : (
          <svg width={svgW} height={svgH} style={{ display: "block", flexShrink: 0 }}>
            {tickVals.map((v, i) => {
              const y = pad.top + innerH - (v / (maxY * 1.1)) * innerH;
              return (
                <g key={i}>
                  <line x1={pad.left} y1={y} x2={pad.left + innerW} y2={y} stroke="#eee" strokeWidth="1" />
                  <text x={pad.left - 8} y={y + 3} textAnchor="end" fontSize="10" fill={ODOO.muted}>{v}</text>
                </g>
              );
            })}
            <text x={pad.left + innerW / 2} y={svgH - 8} textAnchor="middle" fontSize="12" fill={ODOO.muted} fontWeight="600">Stage</text>
            {series.map((s, si) => {
              const x = pad.left + barGap + si * (barW + barGap);
              let yAcc = pad.top + innerH;
              return (
                <g key={s.label}>
                  {s.parts.map((val, oi) => {
                    if (!val) return null;
                    const h = (val / (maxY * 1.1)) * innerH;
                    yAcc -= h;
                    return (
                      <rect
                        key={oi}
                        x={x}
                        y={yAcc}
                        width={barW}
                        height={h}
                        fill={COLORS[oi % COLORS.length]}
                      >
                        <title>{`${s.label} / ${owners[oi]}: ${val}`}</title>
                      </rect>
                    );
                  })}
                  <text
                    x={x + barW / 2}
                    y={svgH - 28}
                    textAnchor="end"
                    fontSize="10"
                    fill={ODOO.text}
                    transform={`rotate(-35 ${x + barW / 2} ${svgH - 28})`}
                  >
                    {s.label.length > 14 ? `${s.label.slice(0, 12)}…` : s.label}
                  </text>
                </g>
              );
            })}
          </svg>
        )}
      </div>
    </div>
  );
};

const PieResponsive = ({ series, areaW, areaH }) => {
  const total = series.reduce((s, x) => s + x.total, 0) || 1;
  // pie plein (pas donut) — taille ~2x plus grande qu'avant
  // on exploite quasi toute la hauteur dispo, cap généreux pour remplir l'intégralité du conteneur
  const avail = Math.min(areaW * 0.62, areaH * 0.88, 580);
  const r = Math.max(120, Math.min(260, avail / 2 - 12));
  const svgSize = r * 2 + 48;
  const cx = svgSize / 2;
  const cy = svgSize / 2;
  let angleAcc = -Math.PI / 2; // démarre en haut (12h) comme Odoo
  return (
    <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`} style={{ flexShrink: 0, overflow: "visible", filter: "drop-shadow(0 1px 2px rgba(0,0,0,.08))" }}>
      {series.map((s, i) => {
        if (!s.total) return null;
        const angle = (s.total / total) * Math.PI * 2;
        // cas mono-tranche = cercle plein
        if (series.length === 1) {
          return <circle key={s.label} cx={cx} cy={cy} r={r} fill={COLORS[0]} stroke="#fff" strokeWidth="2"><title>{`${s.label}: ${s.total}`}</title></circle>;
        }
        const start = angleAcc;
        const end = angleAcc + angle;
        angleAcc = end;
        if (angle < 0.004) return null;
        const x1 = cx + r * Math.cos(start);
        const y1 = cy + r * Math.sin(start);
        const x2 = cx + r * Math.cos(end);
        const y2 = cy + r * Math.sin(end);
        const largeArc = angle > Math.PI ? 1 : 0;
        const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
        return (
          <path key={s.label} d={d} fill={COLORS[i % COLORS.length]} stroke="#fff" strokeWidth="2.5" strokeLinejoin="round">
            <title>{`${s.label}: ${s.total} (${((s.total / total) * 100).toFixed(1)}%)`}</title>
          </path>
        );
      })}
    </svg>
  );
};

const LineResponsive = ({ series, owners, maxY, svgW, svgH, pad }) => {
  const innerW = svgW - pad.left - pad.right;
  const innerH = svgH - pad.top - pad.bottom;
  const pointFor = (value, index) => {
    const x = pad.left + (series.length === 1 ? innerW / 2 : (index / (series.length - 1)) * innerW);
    const y = pad.top + innerH - (value / (maxY * 1.1 || 1)) * innerH;
    return { x, y };
  };
  const pointsForOwner = (ownerIndex) => series.map((s, index) => pointFor(s.parts[ownerIndex] || 0, index));
  const pointsAttr = (points) => points.map(({ x, y }) => `${x},${y}`).join(" ");
  const baseline = pad.top + innerH;
  return (
    <svg width={svgW} height={svgH} style={{ display: "block", flexShrink: 0 }}>
      {/* grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
        const y = pad.top + innerH - t * innerH;
        const v = Math.round(maxY * 1.1 * t);
        return (
          <g key={i}>
            <line x1={pad.left} y1={y} x2={pad.left + innerW} y2={y} stroke="#eee" strokeWidth="1" />
            <text x={pad.left - 8} y={y + 3} textAnchor="end" fontSize="10" fill={ODOO.muted}>{v}</text>
          </g>
        );
      })}
      {owners.map((owner, ownerIndex) => {
        const points = pointsForOwner(ownerIndex);
        const area = `${pad.left},${baseline} ${pointsAttr(points)} ${pad.left + innerW},${baseline}`;
        return (
          <g key={owner}>
            <polygon points={area} fill={COLORS[ownerIndex % COLORS.length]} opacity="0.12" />
            <polyline points={pointsAttr(points)} fill="none" stroke={COLORS[ownerIndex % COLORS.length]} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
            {points.map(({ x, y }, index) => (
              <circle key={series[index].label} cx={x} cy={y} r={4} fill={COLORS[ownerIndex % COLORS.length]} stroke="#fff" strokeWidth="1.5">
                <title>{`${series[index].label} / ${owner}: ${series[index].parts[ownerIndex] || 0}`}</title>
              </circle>
            ))}
          </g>
        );
      })}
      {series.map((s, i) => {
        const { x } = pointFor(0, i);
        return (
          <text key={s.label} x={x} y={svgH - 28} textAnchor="end" fontSize="10" fill={ODOO.text} transform={`rotate(-35 ${x} ${svgH - 28})`}>
            {s.label.length > 14 ? `${s.label.slice(0, 12)}…` : s.label}
          </text>
        );
      })}
      <text x={pad.left + innerW / 2} y={svgH - 8} textAnchor="middle" fontSize="12" fill={ODOO.muted} fontWeight="600">Stage</text>
    </svg>
  );
};

export default CrmV2GraphView;
